import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { Profile } from '@/types/database';
import { createLogger } from '@/lib/logger';
import { setUser as setSentryUser } from '@/lib/errorReporting';
import { identifyUser } from '@/lib/analytics';

const log = createLogger('PROFILE_STORE');

interface ProfileState {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number;
  fetchProfile: (userId: string, force?: boolean) => Promise<Profile | null>;
  updateProfile: (partial: Partial<Profile>) => void;
  clear: () => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  profile: null,
  loading: false,
  error: null,
  lastFetchedAt: 0,

  fetchProfile: async (userId: string, force = false): Promise<Profile | null> => {
    if (!userId) return null;

    // Debounce: don't re-fetch if last fetch was less than 2 seconds ago (unless forced)
    const now = Date.now();
    const state = get();
    if (!force && now - state.lastFetchedAt < 2000 && state.profile) {
      return state.profile;
    }

    set({ loading: true, error: null });

    try {
      let profileData: any = null;

      // Try fetching from the view first
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, handle, display_name, gym_id, avatar_url, onboarding_completed, units, tos_accepted_at, is_admin')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          // Check if view doesn't exist
          const msg = String(error.message ?? '');
          const isMissing = error.code === '42P01' || /does not exist/i.test(msg);
          if (!isMissing) {
            throw error;
          }
        } else if (data) {
          profileData = data;
        }
      } catch (viewError: any) {
        log.warn('View query failed, falling back to profiles table:', viewError);
      }

      // If view failed or doesn't exist, fall back to profiles table
      if (!profileData) {
        const { data: directData, error: directError } = await supabase
          .from('profiles')
          .select('id, handle, display_name, gym_id, avatar_url, onboarding_completed, units, tos_accepted_at, is_admin')
          .eq('id', userId)
          .maybeSingle();

        if (directError) {
          throw directError;
        }

        if (directData) {
          // Map to match view structure
          profileData = {
            user_id: directData.id,
            handle: directData.handle,
            display_name: directData.display_name,
            gym_id: directData.gym_id,
            avatar_url: directData.avatar_url,
            onboarding_completed: (directData as any).onboarding_completed ?? false,
            units: (directData as any).units ?? 'imperial',
            tos_accepted_at: (directData as any).tos_accepted_at ?? null,
            is_admin: (directData as any).is_admin ?? false,
          };
        }
      }

      if (profileData) {
        const row = profileData as Record<string, unknown>;
        const mapped: Partial<Profile> & { id: string } = {
          id: (row.id ?? row.user_id) as string,
          handle: (row.handle as string) ?? undefined,
          display_name: (row.display_name as string) ?? '',
          gym_id: (row.gym_id as string) ?? undefined,
          avatar_url: (row.avatar_url as string) ?? undefined,
          onboarding_completed: (row.onboarding_completed as boolean) ?? false,
          tos_accepted_at: (row.tos_accepted_at as string) ?? undefined,
          is_admin: (row.is_admin as boolean) ?? false,
        };

        // Fetch score/rank fields from the profiles table (RLS: own row)
        try {
          const { data: scoreData } = await supabase
            .from('profiles')
            .select('lifting_score, current_rank, duel_standard_strength_kg, duel_standard_components, duel_standard_updated_at, callus_score, bodyweight, units, gender, bio, training_tags, training_goals, home_gym_id, home_gym_name, home_gym_address, trial_ends_at, subscription_tier, subscription_source, subscription_expires_at, subscription_updated_at, date_of_birth, height_cm, body_fat, phone, address_line1, address_city, address_state, address_zip, onboarding_tier, visibility, freshness_band, badges_count')
            .eq('id', userId)
            .maybeSingle();

          if (scoreData) {
            Object.assign(mapped, scoreData);
          }
        } catch (e) {
          log.warn('Failed to fetch score fields from profiles:', e);
        }

        set({ profile: mapped as Profile, loading: false, error: null, lastFetchedAt: now });
        setSentryUser(mapped.id, undefined);
        identifyUser(mapped.id, { handle: mapped.handle });

        if (__DEV__) {
          log.info('Profile fetched', {
            id: mapped.id,
            handle: mapped.handle,
            lifting_score: mapped.lifting_score,
            duel_standard_strength_kg: mapped.duel_standard_strength_kg,
          });
        }

        return mapped as Profile;
      }

      set({ loading: false, lastFetchedAt: now });
      return null;
    } catch (e: unknown) {
      log.error('fetchProfile exception:', e);
      set({ loading: false, error: e instanceof Error ? e.message : String(e), lastFetchedAt: now });
      return null;
    }
  },

  updateProfile: (partial: Partial<Profile>) => {
    const current = get().profile;
    if (current) {
      set({ profile: { ...current, ...partial } });
    }
  },

  clear: () => set({ profile: null, loading: false, error: null, lastFetchedAt: 0 }),
}));
